import React, { useState } from 'react';
import {
  Container, Grid, Card, CardContent, CardMedia, Chip,
  Typography, Button, Box, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Dialog, DialogContent, DialogActions, IconButton,Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { isDiscountActive, calculateDiscountedPrice, getDiscountBadge } from '../utils/discountUtils';
import noPhoto from "../assets/no_photo.webp";
import ProductOptionsDialog from './ProductOptionsDialog';
import keywordsData from '../lang/keywords.json';
import { useLanguage } from '../context/LanguageContext';
import currencyTrans, { formatPrice, getCurrencySymbol, getCurrencyShort, getCurrencyFullName } from '../components/CarrancyTrans';

const ProductCard = ({ product, addToCart , currency = 'MYR' }) => {
  const [optionsOpen, setOptionsOpen] = useState(false);
  const { language } = useLanguage();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const isDiscounted = isDiscountActive(product);
  const discountedPrice = calculateDiscountedPrice(product);
  const discountBadge = getDiscountBadge(product);
  const [open, setOpen] = useState(false);
  const addToCartWithSaveTxt = language === 'ar' ? 'أضف إلى السلة - خصم!' : 'Save - Add to basket';
  const customizeTxt = language === 'ar' ? 'تخصيص' : 'Customize';
  const viewTxt = language === 'ar' ? 'عرض' : 'View'; 
  const availbleTxt = language === 'ar' ? 'متوفر' : 'Available';
  const unAvailbleTxt = language === 'ar' ? 'غير متوفر' : 'Unavailable';
  const addToCartTxt = language === 'ar' ? 'أضف إلى السلة' : 'Add to basket';
  const addToCartithSaveTxt = language === 'ar' ? 'أضف إلى السلة - خصم!' : 'Save - Add to basket';
  const outOfStockTxt = language === 'ar' ? 'إنتهى من المخزن' : 'Out of stock';
  const reviewProductTxt = language === 'ar' ?  'يرجى مراجعة المنتج قبل الاستخدام': 'Please review the product before use';
  const noAddDescribTxt = language === 'ar' ?  'لا توجد تفاصيل إضافية متاحة': 'No addetional description is available for this product';
  const noDescribTxt = language === 'ar' ? 'لا يوجد وصف متوفر لهذا المنتج': 'No description is available for this product';
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAddToCart = () => {
    addToCart(product.id);
    handleClose();
  };

  // Add these handlers
const handleCustomizeClick = () => {
  setSelectedProduct({
    ...product,
    final_price: discountedPrice || product.price
  });
  setOptionsOpen(true);
};

const handleAddCustomizedToCart = (customizedItem) => {
  // This will call the enhanced addToCart with options
  addToCart(customizedItem);
};

  return (
        <>
      <Grid>
        <Card
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '24px',
            backgroundColor: '#0D0D0D',
            border: '1px solid rgba(255,215,0,0.15)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              borderColor: '#FFD700',
              boxShadow: '0 20px 40px rgba(255,215,0,0.2)',
            }
          }}
        >
          {/* Discount Badge */}
          {discountBadge && (
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 2,
                backgroundColor: '#FFD700',
                color: '#000000',
                fontSize: 12,
                fontWeight: 800,
                padding: '6px 12px',
                borderRadius: '20px',
                border: '2px solid #000000',
                boxShadow: '0 4px 12px rgba(255, 215, 0, 0.4)',
              }}
            >
              {discountBadge}
            </Box>
          )}

          {/* Product Image */}
          <Box sx={{ 
            position: 'relative', 
            height: 200, 
            overflow: 'hidden',
            backgroundColor: '#1A1A1A'
          }}>
            <CardMedia
              component="img"
              image={product.imageUrl || noPhoto}
              alt={product.name}
              sx={{
                height: '100%',
                width: '100%',
                objectFit: 'cover',
                transition: 'transform 0.6s ease',
                '&:hover': {
                  transform: 'scale(1.08)',
                }
              }}
            />
            
            {/* View Overlay */}
            <Box 
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 60%)',
                opacity: 0,
                transition: 'opacity 0.3s ease',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                p: 2,
                '&:hover': {
                  opacity: 1
                }
              }}
            >
              {product.quantity > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleClickOpen}
                  sx={{
                    borderRadius: '30px',
                    backgroundColor: '#FFD700',
                    color: '#000000',
                    fontWeight: 700,
                    px: 3,
                    py: 1,
                    '&:hover': {
                      backgroundColor: '#FFE55C',
                      boxShadow: '0 4px 12px rgba(255, 215, 0, 0.4)',
                    }
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: 18, mr: 1 }} />
                  {viewTxt}
                </Button>
              )}
            </Box>
          </Box>

          <CardContent sx={{ 
            flexGrow: 1, 
            p: 2, 
            display: 'flex', 
            flexDirection: 'column',
            gap: 1
          }}>
            {/* Category */}
            {product.category && (
              <Chip
                label={language === 'ar' ? product.category.name : product.category.name_en}
                size="small"
                sx={{
                  backgroundColor: '#1A1A1A',
                  color: '#B3B3B3',
                  border: '1px solid #2D2D2D',
                  borderRadius: '6px',
                  height: 24,
                  alignSelf: 'flex-start',
                  '&:hover': {
                    borderColor: '#FFD700',
                    color: '#FFD700',
                  }
                }}
              />
            )}
            
            {/* Product Name */}
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                color: '#FFFFFF',
                lineHeight: 1.3,
                height: 44,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {language === 'ar' ? product.name : product.name_en}
            </Typography>

            {/* Promotion Badge */}
            {product?.has_promotion && product?.promotion_is_active === true && (
              <Tooltip title={language === 'ar' ? 'هدية مجانية' : 'Free gift'}>
                <Chip
                  icon={<CardGiftcardIcon sx={{ fontSize: 16 }} />}
                  label={language === 'ar' ? 'هدية مجانية' : 'Free gift'}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255, 215, 0, 0.15)',
                    color: '#FFD700',
                    border: '1px solid #FFD700',
                    fontWeight: 600,
                    height: 24,
                    alignSelf: 'flex-start',
                  }}
                />
              </Tooltip>
            )}

            {/* Description */}
            <Typography 
              variant="body2" 
              sx={{
                color: '#B3B3B3',
                height: 60,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                mb: 1
              }}
            >
              {language === 'ar' ? product.description : product.description_en}
            </Typography>
            
            {/* Price Display */}
            <Box sx={{ mt: 'auto' }}>
              {isDiscounted ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                    <Typography
                      variant="h5"
                      fontWeight="900"
                      sx={{
                        color: '#FFD700',
                        fontSize: '1.4rem',
                      }}
                    >
                      {formatPrice(discountedPrice.toFixed(2),currency,language)}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        textDecoration: 'line-through', 
                        color: '#6B6B6B',
                      }}
                    >
                      {formatPrice(product.price.toFixed(2),currency,language)}
                    </Typography>
                  </Box>
                  <Chip
                    label={language === 'ar' ? `وفر ${product.discount_percent}%` : `Save ${product.discount_percent}%`} 
                    size="small"
                    sx={{
                      backgroundColor: '#FFD700',
                      color: '#000000',
                      fontWeight: 'bold',
                      height: 22,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
              ) : (
                <Typography
                  variant="h5"
                  fontWeight="900"
                  sx={{
                    color: '#FFD700',
                    fontSize: '1.4rem',
                  }}
                >
                  {formatPrice(product.price.toFixed(2),currency,language)}
                </Typography>
              )}
            </Box>
          </CardContent>
          
          {/* Add to Cart Button */}
          <Box sx={{ p: 2, pt: 0 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                if (product.has_sizes || product.has_extras) {
                  handleCustomizeClick();
                } else {
                  addToCart(product.id);
                }
              }}
              disabled={product.quantity <= 0}
              startIcon={product.quantity > 0 && <AddShoppingCartIcon />}
              sx={{
                borderRadius: '30px',
                py: 1.2,
                fontWeight: 700,
                fontSize: '0.9rem',
                background: isDiscounted 
                  ? 'linear-gradient(135deg, #4CAF50, #45a049)' 
                  : 'linear-gradient(135deg, #FFD700, #FFC400)',
                color: '#000000',
                boxShadow: '0 4px 12px rgba(255,215,0,0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(255,215,0,0.5)',
                  background: isDiscounted 
                    ? 'linear-gradient(135deg, #45a049, #3d8b40)' 
                    : 'linear-gradient(135deg, #FFE55C, #FFD700)',
                },
                '&:disabled': {
                  background: '#1A1A1A',
                  color: '#6B6B6B',
                  boxShadow: 'none',
                },
              }}
            >
              {product.quantity <= 0 ? outOfStockTxt : (
                (product.has_sizes || product.has_extras) 
                  ? customizeTxt
                  : (isDiscounted ? addToCartWithSaveTxt : addToCartTxt)
              )}
            </Button>
          </Box>
        </Card>
      </Grid>

      {/* Product Detail Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth 
        dir={language === 'ar' ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: '1px solid #FFD700',
            borderRadius: '20px',
            boxShadow: '0 12px 32px rgba(255, 215, 0, 0.2)',
          }
        }}
      >
        <DialogContent sx={{ p: 4, backgroundColor: '#0D0D0D' }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: '#B3B3B3',
              backgroundColor: '#1A1A1A',
              border: '1px solid #2D2D2D',
              '&:hover': {
                backgroundColor: '#FFD700',
                color: '#000000',
                border: '1px solid #FFD700',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{
                backgroundColor: '#1A1A1A',
                borderRadius: '16px',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 400,
                border: '1px solid #2D2D2D',
              }}>
                <CardMedia
                  component="img"
                  height="100%"
                  image={product.imageUrl || noPhoto}
                  alt={product.name}
                  sx={{ 
                    objectFit: 'contain', 
                    borderRadius: '12px',
                    maxHeight: 380
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom sx={{ 
                color: '#FFD700',
                fontWeight: 900,
                fontSize: '2.2rem',
                mb: 2
              }}>
                {language === 'ar' ? product.name : product.name_en}
              </Typography>
              
              {product.category && (
                <Chip 
                  label={language === 'ar' ? product.category.name : product.category.name_en}
                  sx={{
                    backgroundColor: '#1A1A1A',
                    color: '#FFD700',
                    border: '1px solid #FFD700',
                    fontWeight: 600,
                    mb: 2,
                    borderRadius: '8px'
                  }}
                />
              )}
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ 
                  lineHeight: 1.8, 
                  textAlign: 'justify',
                  color: '#FFFFFF',
                  fontSize: '1.1rem',
                  mb: 3
                }}>
                  {language === 'ar' ? product.description : product.description_en || noDescribTxt }
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ 
                  color: '#FFD700',
                  fontWeight: 700,
                  borderBottom: '2px solid #FFD700',
                  pb: 1,
                  display: 'inline-block'
                }}>
                  {language === 'ar' ? 'التفاصيل:' : 'Details:'}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#B3B3B3',
                  mt: 1,
                  lineHeight: 1.8
                }}>
                  {language === 'ar' ? product.details : product.details_en || noAddDescribTxt}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ 
                  color: '#FFD700',
                  fontWeight: 700,
                  borderBottom: '2px solid #FFD700',
                  pb: 1,
                  display: 'inline-block'
                }}>
                  
                  {language === 'ar' ? 'طريقة الاستخدام:' : 'How to use:'}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#B3B3B3',
                  mt: 1,
                  lineHeight: 1.8
                }}>
                  {language === 'ar' ? product.usageInstructions : product.usageInstructions_en || reviewProductTxt}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  mr: 1,
                  color: '#FFFFFF',
                  fontWeight: 600
                }}>
                  
                  {language === 'ar' ? 'الحالة:' : 'Status:'}
                </Typography>
                <Chip 
                  label={product.quantity > 0 ? availbleTxt : unAvailbleTxt}
                  sx={{
                    backgroundColor: product.quantity > 0 ? '#FFD700' : '#1A1A1A',
                    color: product.quantity > 0 ? '#000000' : '#6B6B6B',
                    fontWeight: 'bold',
                    border: product.quantity > 0 ? '1px solid #FFD700' : '1px solid #2D2D2D',
                  }}
                />
              </Box>
              
              {/* Price Display in Dialog */}
              <Box sx={{ 
                mb: 3, 
                p: 3, 
                backgroundColor: '#1A1A1A',
                border: '1px solid #2D2D2D',
                borderRadius: '16px'
              }}>
                {isDiscounted ? (
                  <Box>
                    <Typography 
                      variant="h2" 
                      fontWeight="900"
                      sx={{ 
                        color: '#FFD700',
                        fontSize: '2.5rem',
                        lineHeight: 1.2
                      }}
                    >
                     {discountedPrice} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                    </Typography>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        textDecoration: 'line-through', 
                        color: '#6B6B6B',
                        mt: 1
                      }}
                    >
                      {product.price.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                    </Typography>
                    <Chip 
                      label={language === 'ar' ? `وفر ${product.discount_percent}%` : `Save ${product.discount_percent}%`}
                      sx={{
                        backgroundColor: '#FFD700',
                        color: '#000000',
                        fontWeight: 'bold',
                        mt: 2,
                        px: 2,
                        py: 1,
                        height: 'auto',
                        fontSize: '1rem',
                        border: '1px solid #000000'
                      }}
                    />
                  </Box>
                ) : (
                  <Typography variant="h2" fontWeight="900" sx={{ color: '#FFD700' }}>
                    {product.price.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          backgroundColor: '#0D0D0D',
          borderTop: '1px solid #2D2D2D'
        }}>
          <Button 
            onClick={handleClose} 
            sx={{
              color: '#B3B3B3',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                color: '#FFD700',
                backgroundColor: 'rgba(255, 215, 0, 0.08)',
              }
            }}
          >
            {language === 'ar' ? 'إغلاق' : 'Close'}
          </Button>
          <Button 
            onClick={() => {
              if (product.has_sizes || product.has_extras) {
                handleCustomizeClick();
              } else {
                handleAddToCart();
              }
            }}
            variant="contained" 
            disabled={product.quantity <= 0}
            startIcon={<AddShoppingCartIcon />}
            size="large"
            sx={{
              backgroundColor: '#FFD700',
              color: '#000000',
              fontWeight: 800,
              px: 4,
              py: 1.5,
              borderRadius: '12px',
              fontSize: '1.1rem',
              '&:hover': {
                backgroundColor: '#FFE55C',
                boxShadow: '0 6px 16px rgba(255, 215, 0, 0.4)',
              },
              '&:disabled': {
                backgroundColor: '#1A1A1A',
                color: '#6B6B6B',
              }
            }}
          >
            {product.quantity <= 0 ? outOfStockTxt : (
              (product.has_sizes || product.has_extras) 
                ? (language === 'ar' ? 'تخصيص' : 'Customize')
                : (isDiscounted ? addToCartithSaveTxt : addToCartTxt)
            )}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Product Options Dialog */}
      <ProductOptionsDialog
        open={optionsOpen}
        onClose={() => setOptionsOpen(false)}
        product={selectedProduct}
        onAddToCart={handleAddCustomizedToCart}
        language={language}
        keywordsData={keywordsData}
      />
    </>
  );
};

export default ProductCard;